<template>
  <section>
    <div class="d-flex justify-content-between align-items-start">
      <h1 class="h2 mb-3">Membership History</h1>
      <button v-if="(userRole === 'Admin' || userRole === 'SuperAdmin')" class="btn btn-primary" @click.prevent="add">Add</button>
    </div>
    <div role="alert" class="alert alert-success" v-if="success">{{success}}</div>
    <div role="alert" class="alert alert-warning" v-else-if="errors.length > 0">
      <ul :class="['mb-0', {'list-unstyled': errors.length === 1}]">
        <li v-for="(error, index) in errors" :key="index">
          {{Object.values(error)[0]}}
        </li>
      </ul>
    </div>
    <data-table :dataRows="rows" :dataColumns="columns" :options="options">
      <tableTextTemplate slot-scope="{ row }" slot="expandedHeader" :text="`${row.membership_options.find(option => option.id === row.membership_type_id).name} ${new Date(row.effective_date.replace(/-/g, '/')).toLocaleString('en-AU',{ year: 'numeric', month: '2-digit', day: 'numeric' })}`" :row="row" :options="{ expandMobile: true }" />
      <div slot-scope="{ row }" slot="expandedRow">
        <ul class="list-unstyled mb-0 pt-2">
          <li>
            <strong>Membership Type: </strong>
            <MembershipType :row="row" />
          </li>
          <li>
            <strong>Effective Date: </strong>
            <EffectiveDate :row="row" />
          </li>
          <li>
            <strong>Valid Until: </strong>
            <span>{{ row.valid_until ? new Date(row.valid_until.replace(/-/g, '/')).toLocaleString('en-AU',{year: 'numeric', month: '2-digit', day: 'numeric'}) : '' }}</span>
          </li>
          <li v-if="row.editable && (userRole === 'Admin' || userRole === 'SuperAdmin')">
            <strong>Action: </strong>
            <button v-if="row.editable && (userRole === 'Admin' || userRole === 'SuperAdmin')" class="btn btn-sm btn-primary" @click="toggleEdit(row)">
              <div v-if="row.processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
              <span v-else>{{ row.editing ? 'Save' : 'Edit' }}</span>
            </button>
            <button v-if="row.editable && (userRole === 'Admin' || userRole === 'SuperAdmin')" class="btn btn-sm btn-primary mt-2" @click="modalData = row; showModal = true">
              <div v-if="row.processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
              <span v-else>Delete</span>
            </button>
          </li>
        </ul>
      </div>
    </data-table>
    <modal v-if="showModal" @close="showModal = false">
      <div slot="body">
        <div class="alert alert-warning">
          You are about to delete the {{ modalData.membership_options.find(option => option.id === modalData.membership_type_id).name }} membership from {{
            new Date(modalData.effective_date.replace(/-/g, '/')).toLocaleString('en-AU',{year: 'numeric', month: '2-digit', day: 'numeric'}) +
            modalData.valid_until
              ? `to ${new Date(modalData.valid_until.replace(/-/g, '/')).toLocaleString('en-AU',{year: 'numeric', month: '2-digit', day: 'numeric'})}`
              : ''
            }}. Proceed with deletion?
        </div>
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-primary" @click.prevent="showModal = false">Cancel</button>
          <button class="btn btn-primary ml-2" @click.prevent="deleteRecord(modalData);showModal = false">Approve</button>
        </div>
      </div>
    </modal>
  </section>
</template>

<script>
import axios from 'axios'
import eventBus from '@/store/EventBus.js'
import dataTable from '@/components/DataTable'
import modal from '@/components/Modal'
import tableButtonTemplate from '@/components/DataTableFields/Button'
import tableTextTemplate from '@/components/DataTableFields/Text'
import MembershipType from './MembershipHistoryFields/MembershipType'
import EffectiveDate from './MembershipHistoryFields/EffectiveDate'

export default {
  name: 'MembersProfileMembershipHistory',
  props: ['memberID', 'membership_options'],
  components: {
    'data-table': dataTable,
    MembershipType,
    EffectiveDate,
    tableButtonTemplate,
    tableTextTemplate,
    modal
  },
  data() {
    return {
      rows: [],
      columns: [
        {
          label: 'Membership Type',
          headerClass: 'd-none d-lg-table-cell',
          filterable: false,
          sortable: false,
          visible: true,
          component: ({
            props: ['row'],
            components: { MembershipType },
            template: `<MembershipType :row="row" />`
          }),
          representedAs: 'membership_type_id'
        },
        {
          label: 'Effective Date',
          field: 'effective_date',
          sortable: false,
          filterable: false,
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { EffectiveDate },
            template: `<EffectiveDate :row="row" />`
          })
        },
        {
          label: 'Valid Until',
          field: 'valid_until',
          sortable: false,
          filterable: false,
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.valid_until ? new Date(row.valid_until.replace(/-/g, '/')).toLocaleString('en-AU',{year: 'numeric', month: '2-digit', day: 'numeric'}) : ' '" :row="row" />`
          })
        },
        {
          label: 'Action',
          sortable: false,
          filterable: false,
          headerClass: 'column-lg-small d-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            data() {
              return {
                userRole: localStorage.role
              }
            },
            methods: {
              edit(rowID) {
                eventBus.$emit('edit', rowID)
              },
              deleteRow(rowID) {
                eventBus.$emit('delete', rowID)
              }
            },
            template: `
            <div class="d-lg-flex">
              <button v-if="row.editable && (userRole === 'Admin' || userRole === 'SuperAdmin')" class="btn btn-sm btn-primary" @click="edit(row)">
                <div v-if="row.processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
                <span v-else>{{ row.editing ? 'Save' : 'Edit' }}</span>
              </button>
              <button v-if="row.editable && (userRole === 'Admin' || userRole === 'SuperAdmin')" class="btn btn-sm btn-primary ml-lg-2" @click="deleteRow(row)">
                <div v-if="row.processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
                <span v-else>Delete</span>
              </button>
            </div>`
          })
        }
      ],
      options: {
        search: false,
        loaded: false
      },
      success: '',
      errors: [],
      userRole: localStorage.role,
      showModal: false,
      modalData: null
    }
  },
  mounted() {
    this.getRows(this.memberID)
    // Edit line
    eventBus.$on('edit', (editRow) => {
      this.toggleEdit(editRow)
    })
    eventBus.$on('delete', (deleteRow) => {
      this.modalData = deleteRow
      this.showModal = true
    })
  },
  beforeDestroy() {
    eventBus.$off('edit')
    eventBus.$off('delete')
  },
  methods: {
    getRows(memberID) {
      // Get history
      this.rows = []
      this.options.loaded = false
      axios.get(`${this.$api}/user-membership/${memberID}`).then((response) => {
        this.options.loaded = true
        if (response.data.status === 'success') {
          this.rows = response.data.data.map((row) => {
            const tempRow = row
            tempRow.membership_options = this.membership_options
            tempRow.editing = false
            tempRow.processing = false
            return tempRow
          })
        }
      }).catch((error) => {
        // eslint-disable-next-line
        console.log(error)
      })
    },
    toggleEdit(editRow) {
      if (editRow.editing) {
        this.save(editRow)
      } else {
        editRow.editing = true
      }
    },
    add() {
      this.rows = this.rows.map((row) => {
        const tempRow = row
        tempRow.editing = false
        return tempRow
      })
      this.rows.splice(0, 0, {
        user_id: this.memberID,
        membership_options: this.membership_options,
        membership_type_id: null,
        effective_date: null,
        editable: true,
        editing: true,
        processing: false
      })
    },
    save(saveRow) {
      if (!saveRow.processing) {
        saveRow.processing = true
        this.success = ''
        this.errors = []
        axios.post(`${this.$api}/admin/${saveRow.id ? 'update' : 'add'}-membership`, saveRow).then((response) => {
          saveRow.processing = false
          saveRow.editing = false
          if (response.data.status === 'success' || response.status === 'success') {
            this.success = 'Record Saved.'
            this.getRows(saveRow.user_id)
          } else if (response.data.status === 'validation_error' || response.data.status === 'error') {
            this.errors = Object.values(response.data.errors)
          }
        }).catch((error) => {
          // eslint-disable-next-line
          console.log(error)
        })
      }
    },
    deleteRecord(deleteRow) {
      if (!deleteRow.processing) {
        deleteRow.processing = true
        this.success = ''
        this.errors = []
        axios.post(`${this.$api}/admin/delete-membership`, { id: deleteRow.id }).then((response) => {
          deleteRow.processing = false
          if (response.data.status === 'success' || response.status === 'success') {
            this.success = 'Record Deleted.'
            this.getRows(deleteRow.user_id)
          } else if (response.data.status === 'validation_error') {
            this.errors = Object.values(response.data.errors)
          }
        }).catch((error) => {
          // eslint-disable-next-line
          console.log(error)
        })
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Members/Tabs/MembershipHistory.vue